import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useErrorHandler, useLoaderToast, ClientError, useQueryClient } from 'shared-ui';

import { updateEventDetails } from 'api/events/events';
import { getAutoExportStatus } from 'api/autoExport/autoExport';
import { AppMessages } from 'i18n/messages';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { useSelectedEvent } from 'hooks/useSelectedEvent/useSelectedEvent';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useQuery } from 'hooks/useQuery/useQuery';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { RcpEnum } from '../eventsFilters/EventsFilters.enum';

import { EventDetails } from './EventDetails';
import { EventDetailsContainerProps, EventDetailsEditForm } from './EventDetails.types';

const AUTO_EXPORT_STATUS_POLL_MS = 10_000;

export const EventDetailsContainer = ({ selectedEvent }: EventDetailsContainerProps) => {
  const { formatMessage } = useLocale();
  const { setSelectedEvent } = useSelectedEvent();
  const queryClient = useQueryClient();
  const { perms } = usePermissions();
  const { load: loadUpdateEvent, update: updateUpdateEvent } = useLoaderToast(`event-${selectedEvent.id}`);
  const { handleError } = useErrorHandler();

  const { data: autoExportStatus } = useQuery([QueryKeyEnum.AUTO_EXPORT_STATUS], getAutoExportStatus, {
    enabled: !!perms?.DEFINICJE?.view,
    refetchInterval: AUTO_EXPORT_STATUS_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const isAutoExportActive = autoExportStatus?.state === 'STARTED';

  const { mutate: onUpdateEvent, isLoading } = useMutation(updateEventDetails, {
    onMutate: () => loadUpdateEvent(formatMessage({ id: AppMessages['event.details.form.loader'] })),
    onSuccess: (_, { body }) => {
      // A changed mode loses the reader's description, as on the backend (WKD-2531).
      const modeChanged = Number(body.tryb) !== Number(selectedEvent.tryb);
      setSelectedEvent({ ...selectedEvent, ...body, ...(modeChanged && { opisTrybu: null }) });
      queryClient.invalidateQueries([QueryKeyEnum.EVENTS]);
      updateUpdateEvent(formatMessage({ id: AppMessages['event.details.form.success'] }));
    },
    onError: (error: ClientError<Error>) => {
      handleError({ error, updateToastCallback: updateUpdateEvent });
    },
  });

  const formMethods = useForm<EventDetailsEditForm>({
    mode: 'all',
    defaultValues: { ...selectedEvent, rcp: selectedEvent.rcp ? RcpEnum.TRUE : RcpEnum.FALSE },
  });

  useEffect(() => {
    formMethods.reset({ ...selectedEvent, rcp: selectedEvent.rcp ? RcpEnum.TRUE : RcpEnum.FALSE });
  }, [selectedEvent]);

  const onSubmit = (body: EventDetailsEditForm) => {
    onUpdateEvent({ id: selectedEvent.id, body: { ...selectedEvent, ...body, rcp: !!Number(body.rcp) } });
  };

  return (
    <FormProvider {...formMethods}>
      <EventDetails
        isLoading={isLoading}
        selectedEvent={selectedEvent}
        onSubmit={onSubmit}
        isAutoExportActive={isAutoExportActive}
      />
    </FormProvider>
  );
};
